import { PhoneInput, isValidPhone } from "@/components/shared/PhoneInput";
import { SearchSelect } from "@/components/shared/SearchSelect";

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Check, CircleCheck, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { Loader } from "@/components/shared/Loader";
import { useAuth } from "@/context/AuthContext";

const APPLICANT_TYPES = [
  "Singapore Citizen",
  "Singapore Permanent Resident",
  "Singapore Resident / Existing Work Pass Holder",
  "International / Foreign Applicant",
];
const PASS_TYPES = ["Employment Pass", "S Pass", "Work Permit", "Other", "None"];
const SKILLS = ["Guest Relations", "Front Desk", "Reservation Management", "Opera/PMS", "Housekeeping",
  "Room Service", "Food & Beverage Service", "Bartending", "Food Preparation", "Kitchen Operations",
  "Customer Service", "Cash Handling", "POS", "Inventory", "Supervisory", "Team Management"];
const PROFICIENCY = ["Basic", "Intermediate", "Good", "Fluent"];
const DEPARTMENTS = ["Front Office", "Housekeeping", "Food & Beverage", "Kitchen", "Engineering", "Security", "Administration", "Sales & Marketing", "Other"];
const MANDATORY_DOCS = ["Passport", "CV / Resume"];

const cls = "w-full rounded-md border border-[var(--aur-border)] bg-[var(--aur-bg-2)] px-3 py-2.5 font-sans text-sm text-[var(--aur-text)] placeholder:text-[var(--aur-text-2)] focus:border-gold focus:outline-none";

function Section({ n, title, children }) {
  return (
    <section className="rounded-lg border border-[var(--aur-border)] bg-[var(--aur-card)] p-6 md:p-8">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gold/15 font-mono text-xs text-gold">{n}</span>
        <h2 className="font-serif text-xl">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="mb-1.5 block font-sans text-xs text-[var(--aur-text-2)]">{label}{required && <span className="text-gold"> *</span>}</label>
      {children}
    </div>
  );
}

export default function JobApplicationForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [alreadyApplied, setAlreadyApplied] = useState(null);
  const [prefilled, setPrefilled] = useState(null);

  const [type, setType] = useState(APPLICANT_TYPES[0]);
  const [f, setF] = useState({
    first_name: user?.name?.split(" ")[0] || "", middle_name: "", last_name: user?.name?.split(" ").slice(1).join(" ") || "",
    dob: "", gender: "", nationality: "", country_of_residence: "", mobile: user?.phone || "", email: user?.email || "",
    address: "", city: "", state: "", country: "", postal_code: "",
    nric_fin: "", id_type: "", id_expiry: "",
    passport_no: "", passport_country: "", passport_issue: "", passport_expiry: "",
    current_title: "", current_employer: "", total_experience: "", hotel_experience: "", current_salary: "", expected_salary: "", notice_period: "",
    highest_qualification: "", institution: "", edu_country: "", year_completed: "", specialization: "",
    pref_department: "", pref_position: "", pref_location: "", pref_employment: "", available_from: "",
    in_singapore: "", holds_pass: "", pass_type: "", pass_number: "", pass_expiry: "",
    worked_in_sg: "", prev_sg_employer: "", prev_sg_position: "", prev_sg_period: "",
  });
  const [experiences, setExperiences] = useState([{ hotel: "", position: "", department: "", country: "", period: "", responsibilities: "" }]);
  const [quals, setQuals] = useState([{ qualification: "", institution: "", country: "", year: "", specialization: "" }]);
  const [languages, setLanguages] = useState([{ language: "English", proficiency: "Good" }]);
  const [skills, setSkills] = useState([]);
  const [otherSkills, setOtherSkills] = useState("");
  const [answers, setAnswers] = useState({});
  const [docs, setDocs] = useState({}); // label -> {id, filename}
  const [declarations, setDeclarations] = useState({ accurate: false, consent: false, understand: false });

  const isForeign = type === "International / Foreign Applicant";

  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => navigate("/careers"));
    api.get("/candidate/applications").then(async (r) => {
      const apps = r.data || [];
      const found = apps.find((a) => a.job_id === id);
      if (found) return setAlreadyApplied(found);
      if (!apps.length) return;
      const { data: last } = await api.get(`/candidate/applications/${apps[0].id}`);
      const fd = last.form_data || {};
      const { experiences: ex, qualifications: q, languages: l, skills: sk, other_skills: os, applicant_type: _t, ...rest } = fd;
      setF((p) => ({ ...p, ...rest }));
      if (last.applicant_type) setType(last.applicant_type);
      if (ex?.length) setExperiences(ex);
      if (q?.length) setQuals(q);
      if (l?.length) setLanguages(l);
      if (sk?.length) setSkills(sk);
      if (os) setOtherSkills(os);
      const prevDocs = {};
      (last.documents || []).forEach((d) => { prevDocs[d.doc_type] = { id: d.id, filename: d.original_filename }; });
      setDocs(prevDocs);
      setPrefilled(last.reference);
    }).catch(() => {});
  }, [id, navigate]);

  if (!job) return <PublicLayout><Loader /></PublicLayout>;

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleSkill = (s) => setSkills((p) => p.includes(s) ? p.filter((x) => x !== s) : [...p, s]);

  const uploadDoc = async (label, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", label);
    try {
      const { data } = await api.post("/candidate/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs((p) => ({ ...p, [label]: { id: data.id, filename: data.original_filename } }));
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    }
  };

  const requiredDocs = [...new Set([...MANDATORY_DOCS, ...(job.required_documents || [])])];
  const questions = job.custom_questions || [];

  const submit = async () => {
    const req = { first_name: "First Name", last_name: "Last Name", dob: "Date of Birth", gender: "Gender", nationality: "Nationality", country_of_residence: "Country of Residence", mobile: "Mobile Number", email: "Email", address: "Current Address", city: "City", country: "Country", postal_code: "Postal Code" };
    for (const [k, label] of Object.entries(req)) {
      if (!f[k]) return toast.error(`${label} is required`);
    }
    if (!isValidPhone(f.mobile)) return toast.error("Enter a valid mobile number with country code");
    if (isForeign) {
      if (!f.passport_no) return toast.error("Passport Number is required");
      if (!f.passport_country) return toast.error("Passport Issuing Country is required");
    } else if (!f.nric_fin) {
      return toast.error("NRIC / FIN Number is required");
    }
    for (const label of requiredDocs.filter((l) => MANDATORY_DOCS.includes(l))) {
      if (!docs[label]) return toast.error(`Please upload: ${label}`);
    }
    for (const q of questions) {
      if (q.required && !answers[q.id]) return toast.error(`Please answer: ${q.question}`);
    }
    if (!declarations.accurate || !declarations.consent || !declarations.understand) {
      return toast.error("Please accept all declarations to submit.");
    }
    setSubmitting(true);
    try {
      const form_data = {
        ...f, applicant_type: type,
        experiences, qualifications: quals, languages,
        skills, other_skills: otherSkills,
      };
      const { data } = await api.post("/candidate/applications", {
        job_id: job.id,
        applicant_type: type,
        form_data,
        document_ids: Object.values(docs).map((d) => d.id),
        additional_answers: answers,
        declarations,
      });
      setResult(data);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (alreadyApplied) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-xl px-6 py-40 text-center">
          <h1 className="font-serif text-3xl tracking-tight">You've already applied</h1>
          <p className="mt-4 font-sans text-sm text-[var(--aur-text-2)]">
            You have an existing application for <b>{job.title}</b> (<span className="font-mono text-gold">{alreadyApplied.reference}</span>).
            Duplicate applications to the same position are not allowed.
          </p>
          <button onClick={() => navigate(`/portal/applications/${alreadyApplied.id}`)} data-testid="already-applied-view" className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3 font-sans text-xs font-semibold uppercase tracking-widest text-[var(--aur-bg)] hover:bg-gold-bright">
            View My Application <ArrowRight size={15} />
          </button>
        </div>
      </PublicLayout>
    );
  }

  if (result) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-xl px-6 py-40 text-center">
          <CircleCheck className="mx-auto text-emerald-400" size={56} />
          <h1 className="mt-6 font-serif text-3xl tracking-tight">Application Submitted Successfully</h1>
          <div className="mt-8 rounded-lg border border-[var(--aur-border)] bg-[var(--aur-card)] p-6 text-left">
            <Row label="Application Reference" value={result.reference} mono />
            <Row label="Position" value={result.job_title} />
            <Row label="Application Status" value="APPLICATION RECEIVED" />
          </div>
          <button onClick={() => navigate(`/portal/applications/${result.id}`)} data-testid="go-to-dashboard" className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3 font-sans text-xs font-semibold uppercase tracking-widest text-[var(--aur-bg)] hover:bg-gold-bright">
            Go to My Dashboard <ArrowRight size={15} />
          </button>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="mx-auto max-w-3xl px-6 pb-24 pt-32 lg:px-8">
        <p className="eyebrow">Job Application</p>
        <h1 className="mt-3 font-serif text-3xl tracking-tight sm:text-4xl">{job.title}</h1>
        {prefilled && (
          <p data-testid="form-prefilled-notice" className="mt-6 rounded-md border border-gold/30 bg-gold/10 px-4 py-3 font-sans text-sm text-gold">
            We've pre-filled this form (including documents) from your previous application {prefilled}. Review and edit anything before submitting.
          </p>
        )}

        <div className="mt-10 space-y-6">
          {/* 1. Job info */}
          <Section n="1" title="Job Information">
            <div className="grid gap-4 sm:grid-cols-2">
              <Row label="Job Title" value={job.title} />
              <Row label="Department" value={job.department} />
              <Row label="Reference" value={job.code} mono />
              <Row label="Location" value={job.location} />
              <Row label="Employment Type" value={job.employment_type} />
              <Row label="Application Date" value={new Date().toLocaleDateString()} />
            </div>
          </Section>

          {/* 2. Applicant type */}
          <Section n="2" title="How are you applying?">
            <div className="grid gap-3 sm:grid-cols-2">
              {APPLICANT_TYPES.map((t) => (
                <button key={t} onClick={() => setType(t)} data-testid={`applicant-type-${t.split(" ")[0].toLowerCase()}`} className={`rounded-md border px-4 py-3 text-left font-sans text-sm transition-all ${type === t ? "border-gold bg-gold/10 text-gold" : "border-[var(--aur-border)] text-[var(--aur-text-2)] hover:border-gold/40"}`}>
                  {type === t && <Check size={14} className="mb-1" />}{t}
                </button>
              ))}
            </div>
          </Section>

          {/* 3. Personal */}
          <Section n="3" title="Personal Information">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="First Name" required><input value={f.first_name} onChange={set("first_name")} className={cls} data-testid="pf-first-name" /></Field>
              <Field label="Middle Name"><input value={f.middle_name} onChange={set("middle_name")} className={cls} /></Field>
              <Field label="Last Name" required><input value={f.last_name} onChange={set("last_name")} className={cls} data-testid="pf-last-name" /></Field>
              <Field label="Date of Birth" required><input type="date" value={f.dob} onChange={set("dob")} className={cls} /></Field>
              <Field label="Gender" required>
                <SearchSelect value={f.gender} onChange={set("gender")} className={cls}><option value="">Select</option><option>Male</option><option>Female</option><option>Prefer not to say</option></SearchSelect>
              </Field>
              <Field label="Nationality" required><input value={f.nationality} onChange={set("nationality")} className={cls} /></Field>
              <Field label="Country of Residence" required><input value={f.country_of_residence} onChange={set("country_of_residence")} className={cls} /></Field>
              <Field label="Mobile Number" required><PhoneInput value={f.mobile} onChange={(v) => setF((p) => ({ ...p, mobile: v }))} className={cls} testid="pf-mobile" required /></Field>
              <Field label="Email Address" required><input type="email" value={f.email} onChange={set("email")} className={cls} data-testid="pf-email" /></Field>
              <Field label="City" required><input value={f.city} onChange={set("city")} className={cls} /></Field>
              <Field label="State/Province"><input value={f.state} onChange={set("state")} className={cls} /></Field>
              <Field label="Country" required><input value={f.country} onChange={set("country")} className={cls} /></Field>
              <Field label="Postal Code" required><input value={f.postal_code} onChange={set("postal_code")} className={cls} /></Field>
            </div>
            <div className="mt-4"><Field label="Current Address" required><textarea value={f.address} onChange={set("address")} rows={2} className={cls} /></Field></div>
          </Section>

          {/* 4. Identification */}
          <Section n="4" title="Identification">
            {isForeign ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Passport Number" required><input value={f.passport_no} onChange={set("passport_no")} className={cls} data-testid="pf-passport" /></Field>
                <Field label="Passport Issuing Country" required><input value={f.passport_country} onChange={set("passport_country")} className={cls} /></Field>
                <Field label="Passport Issue Date"><input type="date" value={f.passport_issue} onChange={set("passport_issue")} className={cls} /></Field>
                <Field label="Passport Expiry Date"><input type="date" value={f.passport_expiry} onChange={set("passport_expiry")} className={cls} /></Field>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="NRIC / FIN Number" required><input value={f.nric_fin} onChange={set("nric_fin")} className={cls} data-testid="pf-nric" /></Field>
                <Field label="ID Type"><SearchSelect value={f.id_type} onChange={set("id_type")} className={cls}><option value="">Select</option><option>NRIC</option><option>FIN</option></SearchSelect></Field>
                <Field label="ID Expiry (if applicable)"><input type="date" value={f.id_expiry} onChange={set("id_expiry")} className={cls} /></Field>
              </div>
            )}
          </Section>

          {/* 5. Professional */}
          <Section n="5" title="Professional Information">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Current / Most Recent Job Title"><input value={f.current_title} onChange={set("current_title")} className={cls} /></Field>
              <Field label="Current / Most Recent Employer"><input value={f.current_employer} onChange={set("current_employer")} className={cls} /></Field>
              <Field label="Total Years of Experience"><input value={f.total_experience} onChange={set("total_experience")} className={cls} /></Field>
              <Field label="Years of Hospitality Experience"><input value={f.hotel_experience} onChange={set("hotel_experience")} className={cls} /></Field>
              <Field label="Current Salary"><input value={f.current_salary} onChange={set("current_salary")} className={cls} /></Field>
              <Field label="Expected Salary"><input value={f.expected_salary} onChange={set("expected_salary")} className={cls} /></Field>
              <Field label="Notice Period"><input value={f.notice_period} onChange={set("notice_period")} className={cls} /></Field>
            </div>
            <p className="mt-6 mb-3 font-sans text-xs uppercase tracking-widest text-[var(--aur-text-2)]">Previous hospitality experience</p>
            {experiences.map((ex, i) => (
              <div key={i} className="mb-3 grid gap-3 rounded-md border border-[var(--aur-border)] p-4 sm:grid-cols-2">
                <input placeholder="Hotel name" value={ex.hotel} onChange={(e) => setExperiences((p) => p.map((x, j) => j === i ? { ...x, hotel: e.target.value } : x))} className={cls} />
                <input placeholder="Position" value={ex.position} onChange={(e) => setExperiences((p) => p.map((x, j) => j === i ? { ...x, position: e.target.value } : x))} className={cls} />
                <input placeholder="Department" value={ex.department} onChange={(e) => setExperiences((p) => p.map((x, j) => j === i ? { ...x, department: e.target.value } : x))} className={cls} />
                <input placeholder="Country" value={ex.country} onChange={(e) => setExperiences((p) => p.map((x, j) => j === i ? { ...x, country: e.target.value } : x))} className={cls} />
                <input placeholder="Employment period" value={ex.period} onChange={(e) => setExperiences((p) => p.map((x, j) => j === i ? { ...x, period: e.target.value } : x))} className={cls} />
                <input placeholder="Responsibilities" value={ex.responsibilities} onChange={(e) => setExperiences((p) => p.map((x, j) => j === i ? { ...x, responsibilities: e.target.value } : x))} className={cls} />
                {experiences.length > 1 && <button onClick={() => setExperiences((p) => p.filter((_, j) => j !== i))} className="flex items-center gap-1 justify-self-start text-xs text-rose-400"><Trash2 size={13} /> Remove</button>}
              </div>
            ))}
            <button onClick={() => setExperiences((p) => [...p, { hotel: "", position: "", department: "", country: "", period: "", responsibilities: "" }])} className="flex items-center gap-1.5 font-sans text-xs text-gold"><Plus size={14} /> Add Another Experience</button>
          </Section>

          {/* 6. Education */}
          <Section n="6" title="Education & Qualifications">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Highest Qualification"><input value={f.highest_qualification} onChange={set("highest_qualification")} className={cls} /></Field>
              <Field label="Institution Name"><input value={f.institution} onChange={set("institution")} className={cls} /></Field>
              <Field label="Country"><input value={f.edu_country} onChange={set("edu_country")} className={cls} /></Field>
              <Field label="Year Completed"><input value={f.year_completed} onChange={set("year_completed")} className={cls} /></Field>
              <Field label="Specialization"><input value={f.specialization} onChange={set("specialization")} className={cls} /></Field>
            </div>
            <p className="mt-6 mb-3 font-sans text-xs uppercase tracking-widest text-[var(--aur-text-2)]">Additional qualifications</p>
            {quals.map((q, i) => (
              <div key={i} className="mb-3 grid gap-3 rounded-md border border-[var(--aur-border)] p-4 sm:grid-cols-2">
                <input placeholder="Qualification (e.g. Diploma, Food Safety)" value={q.qualification} onChange={(e) => setQuals((p) => p.map((x, j) => j === i ? { ...x, qualification: e.target.value } : x))} className={cls} />
                <input placeholder="Institution" value={q.institution} onChange={(e) => setQuals((p) => p.map((x, j) => j === i ? { ...x, institution: e.target.value } : x))} className={cls} />
                <input placeholder="Country" value={q.country} onChange={(e) => setQuals((p) => p.map((x, j) => j === i ? { ...x, country: e.target.value } : x))} className={cls} />
                <input placeholder="Year" value={q.year} onChange={(e) => setQuals((p) => p.map((x, j) => j === i ? { ...x, year: e.target.value } : x))} className={cls} />
                {quals.length > 1 && <button onClick={() => setQuals((p) => p.filter((_, j) => j !== i))} className="flex items-center gap-1 justify-self-start text-xs text-rose-400"><Trash2 size={13} /> Remove</button>}
              </div>
            ))}
            <button onClick={() => setQuals((p) => [...p, { qualification: "", institution: "", country: "", year: "", specialization: "" }])} className="flex items-center gap-1.5 font-sans text-xs text-gold"><Plus size={14} /> Add Qualification</button>
          </Section>

          {/* 7. Preferences */}
          <Section n="7" title="Job Preferences">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Preferred Department"><SearchSelect value={f.pref_department} onChange={set("pref_department")} className={cls}><option value="">Select</option>{DEPARTMENTS.map((d) => <option key={d}>{d}</option>)}</SearchSelect></Field>
              <Field label="Preferred Position"><input value={f.pref_position} onChange={set("pref_position")} className={cls} /></Field>
              <Field label="Preferred Work Location"><input value={f.pref_location} onChange={set("pref_location")} className={cls} /></Field>
              <Field label="Preferred Employment Type"><SearchSelect value={f.pref_employment} onChange={set("pref_employment")} className={cls}><option value="">Select</option><option>Full-time</option><option>Part-time</option><option>Seasonal</option><option>Contract</option></SearchSelect></Field>
              <Field label="Available From"><input type="date" value={f.available_from} onChange={set("available_from")} className={cls} /></Field>
            </div>
          </Section>

          {/* 8. Foreign work-pass */}
          {isForeign && (
            <Section n="8" title="Singapore Immigration / Work Pass">
              <p className="mb-4 rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 font-sans text-xs text-amber-200">Selecting a pass type does not guarantee eligibility or approval. Actual work-pass requirements depend on your circumstances and MOM assessment.</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Are you currently in Singapore?"><SearchSelect value={f.in_singapore} onChange={set("in_singapore")} className={cls}><option value="">Select</option><option>Yes</option><option>No</option></SearchSelect></Field>
                <Field label="Do you currently hold a Singapore work pass?"><SearchSelect value={f.holds_pass} onChange={set("holds_pass")} className={cls}><option value="">Select</option><option>Yes</option><option>No</option></SearchSelect></Field>
                <Field label="Current Pass Type"><SearchSelect value={f.pass_type} onChange={set("pass_type")} className={cls}><option value="">Select</option>{PASS_TYPES.map((p) => <option key={p}>{p}</option>)}</SearchSelect></Field>
                <Field label="Pass / FIN Number"><input value={f.pass_number} onChange={set("pass_number")} className={cls} /></Field>
                <Field label="Pass Expiry Date"><input type="date" value={f.pass_expiry} onChange={set("pass_expiry")} className={cls} /></Field>
                <Field label="Have you previously worked in Singapore?"><SearchSelect value={f.worked_in_sg} onChange={set("worked_in_sg")} className={cls}><option value="">Select</option><option>Yes</option><option>No</option></SearchSelect></Field>
                <Field label="Previous Singapore Employer"><input value={f.prev_sg_employer} onChange={set("prev_sg_employer")} className={cls} /></Field>
                <Field label="Previous Position"><input value={f.prev_sg_position} onChange={set("prev_sg_position")} className={cls} /></Field>
              </div>
            </Section>
          )}

          {/* 9. Skills */}
          <Section n="9" title="Skills">
            <div className="flex flex-wrap gap-2">
              {SKILLS.map((s) => (
                <button key={s} onClick={() => toggleSkill(s)} className={`rounded-full border px-3 py-1.5 font-sans text-xs transition-all ${skills.includes(s) ? "border-gold bg-gold/15 text-gold" : "border-[var(--aur-border)] text-[var(--aur-text-2)] hover:border-gold/40"}`}>{s}</button>
              ))}
            </div>
            <div className="mt-4"><Field label="Additional Skills"><input value={otherSkills} onChange={(e) => setOtherSkills(e.target.value)} className={cls} placeholder="Comma separated" /></Field></div>
          </Section>

          {/* 10. Languages */}
          <Section n="10" title="Languages">
            {languages.map((l, i) => (
              <div key={i} className="mb-3 grid grid-cols-2 gap-3">
                <input placeholder="Language" value={l.language} onChange={(e) => setLanguages((p) => p.map((x, j) => j === i ? { ...x, language: e.target.value } : x))} className={cls} />
                <div className="flex gap-2">
                  <SearchSelect value={l.proficiency} onChange={(e) => setLanguages((p) => p.map((x, j) => j === i ? { ...x, proficiency: e.target.value } : x))} className={cls}>{PROFICIENCY.map((p) => <option key={p}>{p}</option>)}</SearchSelect>
                  {languages.length > 1 && <button onClick={() => setLanguages((p) => p.filter((_, j) => j !== i))} className="text-rose-400"><Trash2 size={15} /></button>}
                </div>
              </div>
            ))}
            <button onClick={() => setLanguages((p) => [...p, { language: "", proficiency: "Basic" }])} className="flex items-center gap-1.5 font-sans text-xs text-gold"><Plus size={14} /> Add Language</button>
          </Section>

          {/* 11. Documents */}
          <Section n="11" title="Documents">
            <p className="mb-4 font-sans text-xs text-[var(--aur-text-2)]">Private storage · PDF, DOC, DOCX, JPG, PNG · max 10MB. Documents marked * are mandatory; others are optional but help your application.</p>
            <div className="space-y-2">
              {requiredDocs.map((label) => (
                <div key={label} className="flex items-center justify-between rounded-md border border-[var(--aur-border)] bg-[var(--aur-bg-2)] px-4 py-3">
                  <span className="flex items-center gap-2 font-sans text-sm">{docs[label] ? <Check size={15} className="text-emerald-400" /> : <span className={`h-2 w-2 rounded-full ${MANDATORY_DOCS.includes(label) ? "bg-amber-400" : "bg-slate-500"}`} />}{label}{MANDATORY_DOCS.includes(label) ? <span className="text-gold" data-testid={`doc-mandatory-${label.split(" ")[0].toLowerCase()}`}>*</span> : <span className="text-xs text-[var(--aur-text-2)]">(optional)</span>}{docs[label] && <span className="text-xs text-[var(--aur-text-2)]">· {docs[label].filename}</span>}</span>
                  <label className="cursor-pointer rounded-md border border-[var(--aur-border)] px-3 py-1.5 font-sans text-xs text-gold hover:bg-gold/10">
                    <span className="flex items-center gap-1.5"><Upload size={13} /> {docs[label] ? "Replace" : "Upload"}</span>
                    <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" data-testid={`doc-upload-${label.split(" ")[0].toLowerCase()}`} onChange={(e) => uploadDoc(label, e.target.files[0])} />
                  </label>
                </div>
              ))}
              {requiredDocs.length === 0 && <p className="font-sans text-sm text-[var(--aur-text-2)]">No specific documents required — you may add them later from your portal.</p>}
            </div>
          </Section>

          {/* 12. Additional questions */}
          {questions.length > 0 && (
            <Section n="12" title="Additional Questions">
              <div className="space-y-4">
                {questions.map((q) => (
                  <div key={q.id}>
                    <p className="mb-2 font-sans text-sm">{q.question}{q.required && <span className="text-gold"> *</span>}</p>
                    <div className="flex gap-2">
                      {["Yes", "No"].map((opt) => (
                        <button key={opt} onClick={() => setAnswers((p) => ({ ...p, [q.id]: opt }))} data-testid={`answer-${q.id}-${opt.toLowerCase()}`} className={`rounded-md border px-4 py-1.5 font-sans text-xs transition-all ${answers[q.id] === opt ? "border-gold bg-gold/15 text-gold" : "border-[var(--aur-border)] text-[var(--aur-text-2)]"}`}>{opt}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Declaration */}
          <Section n="✓" title="Declaration">
            <div className="space-y-3">
              {[
                ["accurate", "I confirm that the information provided in this application is accurate and complete to the best of my knowledge."],
                ["consent", "I consent to the processing of my personal information for recruitment and employment-related purposes."],
                ["understand", "I understand that submission of this application does not guarantee employment or selection."],
              ].map(([k, text]) => (
                <label key={k} className="flex cursor-pointer items-start gap-3 font-sans text-sm text-[var(--aur-text-2)]">
                  <input type="checkbox" checked={declarations[k]} onChange={(e) => setDeclarations((p) => ({ ...p, [k]: e.target.checked }))} data-testid={`decl-${k}`} className="mt-1 accent-[#C5A059]" />
                  {text}
                </label>
              ))}
            </div>
            <button onClick={submit} disabled={submitting} data-testid="submit-application" className="mt-8 w-full rounded-full bg-gold px-6 py-4 font-sans text-xs font-semibold uppercase tracking-widest text-[var(--aur-bg)] transition-all hover:bg-gold-bright disabled:opacity-60">
              {submitting ? "Submitting…" : "Submit Application"}
            </button>
          </Section>
        </div>
      </div>
    </PublicLayout>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="border-b border-[var(--aur-border)] py-2 last:border-0">
      <p className="font-sans text-[11px] uppercase tracking-widest text-[var(--aur-text-2)]">{label}</p>
      <p className={`mt-0.5 ${mono ? "font-mono text-gold" : "font-sans"} text-sm text-[var(--aur-text)]`}>{value || "—"}</p>
    </div>
  );
}
